import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { BellRing, Check, DoorOpen, MessageCircle, Receipt, ShoppingBag, Users, X } from 'lucide-react';
import { AuditTimeline, Button, Drawer, Modal } from '../../../admin-ui';
import { api } from '../../../services/api';
import { prepStationOf, STATE_LABEL } from './serviceColors';
import InvoiceModal from './InvoiceModal';
import StaffCartaSheet from './StaffCartaSheet';

function formatMinsAgo(requestedAt) {
  if (!requestedAt) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(requestedAt).getTime()) / 60000));
  if (mins === 0) return 'ahora';
  return `${mins} min`;
}

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function shortOrderId(order) {
  return String(order?._id || '').slice(-5) || 'pedido';
}

function timelineItems(events, orders = []) {
  const ordersById = new Map(orders.map((order) => [String(order._id), order]));
  return events.map((event) => ({
    title: event.label,
    time: new Date(event.at).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }),
    description: event.by ? `Origen: ${event.by}` : '',
    order: event.orderId ? ordersById.get(String(event.orderId)) : null,
    clickable: Boolean(event.orderId && ordersById.has(String(event.orderId)))
  }));
}

function OrderDetailModal({ order, table, onClose }) {
  if (!order) return null;
  const createdAt = order.createdAt
    ? new Date(order.createdAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

  return (
    <Modal
      isOpen={Boolean(order)}
      onClose={onClose}
      title={`Pedido ${shortOrderId(order)}`}
      size="lg"
    >
      <div className="service-order-detail">
        <div className="service-order-detail-meta">
          <div><span>Mesa</span><strong>{table?.number || order.tableId?.number || '—'}</strong></div>
          <div><span>Estado</span><strong>{STATE_LABEL[order.status] || order.status}</strong></div>
          <div><span>Hora</span><strong>{createdAt}</strong></div>
          <div><span>Total</span><strong>{formatMoney(order.total)}</strong></div>
        </div>

        <div className="service-order-detail-items">
          {(order.items || []).map((item, index) => {
            const station = prepStationOf(item);
            return (
              <div key={`${order._id}-${item.productId?._id || item.productId || index}`} className="service-order-detail-item">
                <span className={`service-order-detail-station is-${station}`}>
                  {station === 'barra' ? 'Barra' : 'Cocina'}
                </span>
                <strong>{Number(item.quantity || 0).toLocaleString('es-ES')}x</strong>
                <span className="service-order-detail-name">{item.nameSnapshot}</span>
                <em>{formatMoney(Number(item.priceSnapshot || 0) * Number(item.quantity || 1))}</em>
                <small>{STATE_LABEL[item.status || order.status] || item.status || order.status}</small>
                {item.notes && <p>{item.notes}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

export default function MesaDrawer({ table, isOpen, onClose, onOpenTable, onCloseTable, onRefresh }) {
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState('');
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedTimelineOrder, setSelectedTimelineOrder] = useState(null);
  const [staffCartaOpen, setStaffCartaOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [tableSubmitting, setTableSubmitting] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const sessionId = table?.currentSessionId?._id;
  const hasOpenRequest = table?.status === 'open_requested' && !!table?.openRequest;

  const handleAcceptRequest = async () => {
    if (!table?._id) return;
    setRequestSubmitting(true);
    setError('');
    try {
      await api.post(`/tables/${table._id}/accept-open-request`, {});
      onRefresh?.();
      onClose();
    } catch (err) {
      setError(err.data?.message || err.message || 'No se pudo aceptar la solicitud');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!table?._id) return;
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    setRequestSubmitting(true);
    setError('');
    try {
      await api.post(`/tables/${table._id}/reject-open-request`, { reason: rejectReason || undefined });
      onRefresh?.();
      onClose();
      setShowRejectInput(false);
      setRejectReason('');
    } catch (err) {
      setError(err.data?.message || err.message || 'No se pudo rechazar la solicitud');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const loadTimeline = useCallback(() => {
    if (!isOpen || !sessionId) {
      setTimeline(null);
      setSelectedTimelineOrder(null);
      return Promise.resolve();
    }
    setError('');
    return api.get(`/sessions/${sessionId}/timeline`)
      .then(setTimeline)
      .catch((err) => setError(err.message || 'No se pudo cargar el historial'));
  }, [isOpen, sessionId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const sessionOrders = timeline?.orders || table?.currentSessionId?.orders || [];
  const timelineDisplayItems = useMemo(
    () => timelineItems(timeline?.events || [], sessionOrders),
    [timeline?.events, sessionOrders]
  );

  const totals = useMemo(() => {
    return sessionOrders.reduce((acc, order) => {
      acc.total += order.status !== 'cancelled' ? Number(order.total || 0) : 0;
      acc.live += !['paid', 'cancelled'].includes(order.status) ? 1 : 0;
      if (order.status !== 'cancelled') {
        order.items?.forEach((item) => {
          acc.unpaidItems += Math.max(0, Number(item.quantity || 0) - Number(item.invoicedQuantity || 0));
        });
      }
      return acc;
    }, { total: 0, live: 0, unpaidItems: 0 });
  }, [sessionOrders]);

  const closeTable = async () => {
    if (!table?.active) return;
    try {
      setError('');
      await onCloseTable(table);
      await onRefresh?.();
      onClose();
    } catch (err) {
      setError(err.data?.message || err.message || 'No se pudo cerrar la mesa');
    }
  };

  const openTable = async () => {
    if (!table?._id || table.active || hasOpenRequest) return;
    setTableSubmitting(true);
    setError('');
    try {
      await onOpenTable(table);
      await onRefresh?.();
    } catch (err) {
      setError(err.data?.message || err.message || 'No se pudo abrir la mesa');
    } finally {
      setTableSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => {
        setStaffCartaOpen(false);
        onClose();
      }}
      title={table ? `Mesa ${table.number}` : 'Mesa'}
    >
      {!table ? null : (
        <div className="service-drawer">
          {hasOpenRequest && (
            <motion.div
              className="service-drawer-request"
              data-priority={(() => {
                const m = Math.floor((Date.now() - new Date(table.openRequest.requestedAt).getTime()) / 60000);
                return m >= 5 ? 'high' : m >= 3 ? 'mid' : 'low';
              })()}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 320 }}
            >
              <div className="service-drawer-request-header">
                <BellRing size={18} />
                <h3>Solicitud de apertura</h3>
                <span className="service-drawer-request-time">{formatMinsAgo(table.openRequest.requestedAt)}</span>
              </div>
              <div className="service-drawer-request-body">
                <div className="service-drawer-request-row">
                  <Users size={16} />
                  <strong>{table.openRequest.customerName}</strong>
                  <span>· {table.openRequest.partySize} {table.openRequest.partySize === 1 ? 'persona' : 'personas'}</span>
                </div>
                {table.openRequest.customerNote && (
                  <div className="service-drawer-request-row service-drawer-request-note">
                    <MessageCircle size={14} />
                    <em>{table.openRequest.customerNote}</em>
                  </div>
                )}
              </div>
              {showRejectInput && (
                <div className="service-drawer-request-reason">
                  <input
                    type="text"
                    placeholder="Motivo (opcional, máx 200)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    maxLength={200}
                    autoFocus
                  />
                </div>
              )}
              <div className="service-drawer-request-actions">
                <motion.button
                  type="button"
                  className="service-drawer-request-accept"
                  onClick={handleAcceptRequest}
                  disabled={requestSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Check size={16} /> {showRejectInput ? 'Aceptar' : 'Aceptar y abrir'}
                </motion.button>
                <motion.button
                  type="button"
                  className="service-drawer-request-reject"
                  onClick={handleRejectRequest}
                  disabled={requestSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <X size={16} /> {showRejectInput ? 'Confirmar rechazo' : 'Rechazar'}
                </motion.button>
              </div>
            </motion.div>
          )}

          {!hasOpenRequest && (
            <div className="service-drawer-stats">
              <div><span>Total</span><strong>{totals.total.toFixed(2)} €</strong></div>
              <div><span>Activas</span><strong>{totals.live}</strong></div>
              <div><span>Sin facturar</span><strong>{totals.unpaidItems}</strong></div>
              <div><span>Estado</span><strong>{table.active ? 'Abierta' : 'Cerrada'}</strong></div>
            </div>
          )}

          {table.currentSessionId?.requestedBy && !hasOpenRequest && (
            <div className="service-drawer-request-snapshot">
              <BellRing size={13} />
              <span>Sesión iniciada a petición de <strong>{table.currentSessionId.requestedBy.customerName}</strong> ({table.currentSessionId.requestedBy.partySize} pers.)</span>
            </div>
          )}

          {error && <div className="service-error">{error}</div>}

          {!hasOpenRequest && !table.active && (
            <div className="service-drawer-empty">
              <div>
                <strong>Mesa cerrada</strong>
                <span>Abre la mesa desde aquí para activar pedidos por QR y empezar la sesión.</span>
              </div>
              <Button
                variant="primary"
                iconLeft={<DoorOpen size={16} />}
                loading={tableSubmitting}
                onClick={openTable}
              >
                Abrir mesa
              </Button>
            </div>
          )}

          {!hasOpenRequest && sessionOrders.length > 0 && (
            <div className="service-drawer-orders">
              <h4>Pedidos de la sesión</h4>
              {sessionOrders.map((order) => (
                <div key={order._id} className="service-drawer-order">
                  <span>{STATE_LABEL[order.status] || order.status}</span>
                  <strong>{Number(order.total || 0).toFixed(2)} €</strong>
                </div>
              ))}
            </div>
          )}

          {!hasOpenRequest && timeline?.events?.length > 0 && (
            <div className="service-drawer-timeline">
              <h4>Timeline</h4>
              <AuditTimeline
                items={timelineDisplayItems}
                onItemClick={(item) => {
                  if (item.order) setSelectedTimelineOrder(item.order);
                }}
              />
            </div>
          )}

          {table.active && (
            <div className="service-drawer-actions">
              <Button
                variant="secondary"
                className="service-take-order"
                iconLeft={<ShoppingBag size={16} />}
                onClick={() => setStaffCartaOpen(true)}
              >
                Tomar pedido
              </Button>
              <Button
                variant="primary"
                className="service-collect-table"
                iconLeft={<Receipt size={16} />}
                onClick={() => setInvoiceOpen(true)}
                disabled={!table.currentSessionId || totals.unpaidItems <= 0}
                title={totals.unpaidItems <= 0 ? 'No hay items pendientes de cobro' : 'Cobrar y emitir ticket o factura'}
              >
                Cobrar mesa
              </Button>
              <button
                className="service-close-table"
                type="button"
                onClick={closeTable}
                disabled={totals.unpaidItems > 0}
                title={totals.unpaidItems > 0 ? 'Hay items sin facturar' : 'Cerrar mesa'}
              >
                Cerrar mesa
              </button>
            </div>
          )}
          <InvoiceModal
            isOpen={invoiceOpen}
            table={table}
            session={timeline?.session || table.currentSessionId}
            onClose={() => setInvoiceOpen(false)}
            onIssued={async () => {
              setInvoiceOpen(false);
              await loadTimeline();
              await onRefresh?.();
            }}
          />
          <OrderDetailModal
            order={selectedTimelineOrder}
            table={table}
            onClose={() => setSelectedTimelineOrder(null)}
          />
          <StaffCartaSheet
            table={table}
            isOpen={staffCartaOpen}
            onClose={() => {
              setStaffCartaOpen(false);
              loadTimeline();
              onRefresh?.();
            }}
          />
        </div>
      )}
    </Drawer>
  );
}
